<template>
    <tailwind-layout title="Owner Verification">
        <template #header>
            <div class="mb-4 px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center">
                <h1 class="font-semibold text-2xl leading-tight">Complete Owner Verification</h1>
                <div class="flex items-center space-x-4">
                    <inertia-link
                        class="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                        :href="route('payment-methods.index')">
                        <FontAwesome :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>
            </div>
        </template>

        <div class="overflow-hidden shadow-xl rounded-lg p-6 bg-white">
            <p class="text-sm text-gray-600 mb-6">
                Your Plaid account was created but owner verification is incomplete. Please review and submit owner information below so you can continue onboarding.
            </p>

            <form @submit.prevent="store" class="space-y-8 divide-y divide-gray-200">
                <div
                    v-for="(person, index) in form.people"
                    :key="personKey(person, index)"
                    class="pt-8 first:pt-0">
                    <div class="flex items-center justify-between mb-4">
                        <div class="flex items-center gap-3">
                            <h2 class="text-lg font-medium text-gray-900">
                                Person {{ business_type === 'business' ? index + 1 : '' }} Information
                            </h2>
                            <span
                                v-if="person.person_id"
                                class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset bg-green-50 text-green-700 ring-green-600/20">
                                Verified
                            </span>
                        </div>
                        <button
                            v-if="business_type === 'business' && !person.person_id && form.people.length > 1"
                            type="button"
                            class="text-sm text-red-600 hover:text-red-800"
                            @click="removePerson(index)">
                            Remove This Person
                        </button>
                    </div>

                    <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        <div v-if="business_type === 'business'" class="sm:col-span-2">
                            <fieldset :disabled="!!person.person_id">
                                <p class="block text-sm font-medium text-gray-700 flex">
                                    Relationship <span class="text-red-700">*</span>
                                </p>
                                <div class="mt-1 space-y-4">
                                    <div class="flex items-center">
                                        <input
                                            :id="`relationship-control-${index}`"
                                            v-model="person.relationship"
                                            value="CONTROL_PERSON"
                                            type="radio"
                                            class="relative size-2 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden">
                                        <label :for="`relationship-control-${index}`" class="ml-2 block text-sm text-gray-900">Control Person</label>
                                    </div>
                                    <div class="flex items-center">
                                        <input
                                            :id="`relationship-owner-${index}`"
                                            v-model="person.relationship"
                                            value="BENEFICIAL_OWNER"
                                            type="radio"
                                            class="relative size-2 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden">
                                        <label :for="`relationship-owner-${index}`" class="ml-2 block text-sm text-gray-900">Beneficial Owner</label>
                                    </div>
                                </div>
                                <p v-if="form.errors[`people.${index}.relationship`]" class="text-red-500 text-sm">{{ form.errors[`people.${index}.relationship`] }}</p>
                            </fieldset>
                        </div>

                        <div v-if="business_type === 'business'" class="sm:col-span-2">
                            <label :for="`percentage-${index}`" class="block text-sm font-medium text-gray-700">
                                Percentage <span class="text-red-700">*</span>
                            </label>
                            <div class="mt-1">
                                <select
                                    :id="`percentage-${index}`"
                                    v-model="person.percentage"
                                    :disabled="!!person.person_id"
                                    class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100">
                                    <option v-for="value in percentageOptions" :key="value" :value="String(value)">{{ value }}</option>
                                </select>
                                <p v-if="form.errors[`people.${index}.percentage`]" class="text-red-500 text-sm">{{ form.errors[`people.${index}.percentage`] }}</p>
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label :for="`first_name-${index}`" class="block text-sm font-medium text-gray-700">First Name <span class="text-red-700">*</span></label>
                            <input :id="`first_name-${index}`" v-model="person.first_name" type="text" :disabled="!!person.person_id" class="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100">
                            <p v-if="form.errors[`people.${index}.first_name`]" class="text-red-500 text-sm">{{ form.errors[`people.${index}.first_name`] }}</p>
                        </div>

                        <div class="sm:col-span-2">
                            <label :for="`last_name-${index}`" class="block text-sm font-medium text-gray-700">Last Name <span class="text-red-700">*</span></label>
                            <input :id="`last_name-${index}`" v-model="person.last_name" type="text" :disabled="!!person.person_id" class="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100">
                            <p v-if="form.errors[`people.${index}.last_name`]" class="text-red-500 text-sm">{{ form.errors[`people.${index}.last_name`] }}</p>
                        </div>

                        <div class="sm:col-span-2">
                            <label :for="`email-${index}`" class="block text-sm font-medium text-gray-700">Email <span class="text-red-700">*</span></label>
                            <input :id="`email-${index}`" v-model="person.email" type="email" :disabled="!!person.person_id" class="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100">
                            <p v-if="form.errors[`people.${index}.email`]" class="text-red-500 text-sm">{{ form.errors[`people.${index}.email`] }}</p>
                        </div>

                        <div class="sm:col-span-2">
                            <label :for="`dob-${index}`" class="block text-sm font-medium text-gray-700">Date of Birth <span class="text-red-700">*</span></label>
                            <input :id="`dob-${index}`" v-model="person.dob" type="date" :disabled="!!person.person_id" class="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100">
                            <p v-if="form.errors[`people.${index}.dob`]" class="text-red-500 text-sm">{{ form.errors[`people.${index}.dob`] }}</p>
                        </div>

                        <div class="sm:col-span-2">
                            <label :for="`phone-${index}`" class="block text-sm font-medium text-gray-700">Phone Number <span class="text-red-700">*</span></label>
                            <input :id="`phone-${index}`" v-model="person.phone" type="text" :disabled="!!person.person_id" class="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100">
                            <p v-if="form.errors[`people.${index}.phone`]" class="text-red-500 text-sm">{{ form.errors[`people.${index}.phone`] }}</p>
                        </div>

                        <div class="sm:col-span-2">
                            <label :for="`ssn-${index}`" class="block text-sm font-medium text-gray-700">Social Security Number <span v-if="!person.person_id" class="text-red-700">*</span></label>
                            <div v-if="person.person_id" class="mt-1 text-sm text-gray-700">
                                ***-**-{{ person.ssn_last_4 }}
                            </div>
                            <div v-else>
                                <input :id="`ssn-${index}`" v-model="person.ssn" type="text" class="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md">
                                <p class="text-gray-500 text-xs">Do not include dashes. We do not store your SSN on our servers.</p>
                                <p v-if="form.errors[`people.${index}.ssn`]" class="text-red-500 text-sm">{{ form.errors[`people.${index}.ssn`] }}</p>
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <label :for="`address_1-${index}`" class="block text-sm font-medium text-gray-700">Address 1 <span class="text-red-700">*</span></label>
                            <input :id="`address_1-${index}`" v-model="person.address_1" type="text" :disabled="!!person.person_id" class="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100">
                            <p v-if="form.errors[`people.${index}.address_1`]" class="text-red-500 text-sm">{{ form.errors[`people.${index}.address_1`] }}</p>
                        </div>

                        <div class="sm:col-span-2">
                            <label :for="`address_2-${index}`" class="block text-sm font-medium text-gray-700">Address 2</label>
                            <input :id="`address_2-${index}`" v-model="person.address_2" type="text" :disabled="!!person.person_id" class="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100">
                            <p v-if="form.errors[`people.${index}.address_2`]" class="text-red-500 text-sm">{{ form.errors[`people.${index}.address_2`] }}</p>
                        </div>

                        <div class="sm:col-span-2">
                            <label :for="`city-${index}`" class="block text-sm font-medium text-gray-700">City <span class="text-red-700">*</span></label>
                            <input :id="`city-${index}`" v-model="person.city" type="text" :disabled="!!person.person_id" class="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100">
                            <p v-if="form.errors[`people.${index}.city`]" class="text-red-500 text-sm">{{ form.errors[`people.${index}.city`] }}</p>
                        </div>

                        <div class="sm:col-span-2">
                            <label :for="`state-${index}`" class="block text-sm font-medium text-gray-700">State <span class="text-red-700">*</span></label>
                            <select
                                :id="`state-${index}`"
                                v-model="person.state"
                                :disabled="!!person.person_id"
                                class="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100">
                                <option v-for="(state, stateCode) in states" :key="stateCode" :value="stateCode">{{ state }}</option>
                            </select>
                            <p v-if="form.errors[`people.${index}.state`]" class="text-red-500 text-sm">{{ form.errors[`people.${index}.state`] }}</p>
                        </div>

                        <div class="sm:col-span-2">
                            <label :for="`postal_code-${index}`" class="block text-sm font-medium text-gray-700">Postal Code <span class="text-red-700">*</span></label>
                            <input :id="`postal_code-${index}`" v-model="person.postal_code" type="text" :disabled="!!person.person_id" class="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100">
                            <p v-if="form.errors[`people.${index}.postal_code`]" class="text-red-500 text-sm">{{ form.errors[`people.${index}.postal_code`] }}</p>
                        </div>
                    </div>
                </div>

                <div v-if="business_type === 'business'" class="pt-6">
                    <button type="button" class="text-sm font-medium text-indigo-600 hover:text-indigo-800" @click="addPerson">
                        Add Another Person
                    </button>
                </div>

                <div class="pt-5">
                    <div class="flex justify-end">
                        <inertia-link
                            :href="route('payment-methods.index')"
                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            Cancel
                        </inertia-link>
                        <form-button :form="form" type="submit" class="ml-3">
                            Submit Owner Verification
                        </form-button>
                    </div>
                </div>
            </form>
        </div>
    </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import FormButton from '@/Shared/FormButton.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons'

export default {
    props: {
        states: Object,
        business_type: String,
        people: Array,
    },
    components: {
        TailwindLayout,
        FormButton,
        FontAwesome: FontAwesomeIcon,
    },
    data() {
        return {
            form: this.$inertia.form({
                people: this.people.map((person) => ({
                    id: person.id,
                    person_id: person.person_id,
                    relationship: person.relationship || 'CONTROL_PERSON',
                    percentage: person.percentage || (this.business_type === 'individual' ? '100' : null),
                    first_name: person.first_name,
                    last_name: person.last_name,
                    email: person.email,
                    ssn: null,
                    dob: person.dob,
                    phone: person.phone,
                    ssn_last_4: person.ssn_last_4,
                    address_1: person.address_1,
                    address_2: person.address_2,
                    city: person.city,
                    state: person.state,
                    postal_code: person.postal_code,
                })),
            }),
            faChevronLeft,
            percentageOptions: Array.from({ length: 76 }, (_, i) => 100 - i),
        }
    },
    methods: {
        personKey(person, index) {
            return person.id ? `person-${person.id}` : `new-person-${index}`
        },
        emptyPerson() {
            return {
                id: null,
                person_id: null,
                relationship: 'CONTROL_PERSON',
                percentage: null,
                first_name: null,
                last_name: null,
                email: null,
                ssn: null,
                dob: null,
                phone: null,
                ssn_last_4: null,
                address_1: null,
                address_2: null,
                city: null,
                state: null,
                postal_code: null,
            }
        },
        addPerson() {
            this.form.people.push(this.emptyPerson())
        },
        removePerson(index) {
            this.form.people.splice(index, 1)
        },
        store() {
            const payload = {
                people: this.form.people.map((person) => {
                    const entry = { ...person }

                    if (person.person_id) {
                        delete entry.ssn
                    }

                    return entry
                }),
            }

            this.form.transform(() => payload).post(this.route('plaid.owner-verification.store'))
        },
    },
}
</script>
